<script setup lang="ts">
import { formatDate } from '@/helpers/formating'
import GwBtns from '@/components/lib/GwBtns.vue'
import { i18n } from '@/locales/i18n'

import CardRowSimple from '@/components/CardRow/CardRowSimple.vue'
import GwFilterBtnsItem from '@/components/DataTables/Filters/new/GwFilterBtnsItem.vue'
import LoaderCircles from '@/components/Loaders/LoaderCircles.vue'
import type { FiltersOptionsProps } from './GwFilterPickerComponent.vue'

interface GwFilterSelectionComponentProps {
  field: FiltersOptionsProps
  loading: boolean
}
defineProps<GwFilterSelectionComponentProps>()

const emit = defineEmits(['update:field'])
const {
  global: { t },
} = i18n

const handleUpdate = ({
  field,
  item,
  values,
  rangeValues,
}: Partial<{
  field: FiltersOptionsProps
  item: string
  values: any[]
  rangeValues: any[]
}>) => {
  emit('update:field', { field, item, values, rangeValues })
}
</script>

<template>
  <div
    class="gw_filter_selectedoptions_content"
    @click.stop
  >
    <div
      v-if="field.picker === 'UrlValueExposed' && field.value"
      class="gw_btn"
      style="font-size: 0.875rem; --height: 1.5rem; --padding: 0 0.5rem; margin-left: 0.25rem"
      @click="handleUpdate({ field, values: [] })"
    >
      <GwFilterBtnsItem
        :field
        :item="field.options[0]"
      ></GwFilterBtnsItem>
      <div class="icon_btn mdi mdi-close ml-1"></div>
    </div>

    <GwBtns
      v-if="field.filterPart && field.selection === 'GwBtns'"
      class="gwbtns_horizontal_white"
      :options="field.selectedOptions"
      :tstPrefix="`filterselection_${field.field_name}`"
      style="padding: 0; --gwbtns-wrap: wrap"
      @click="
        (_e, item) => {
          handleUpdate({ field, item })
        }
      "
    >
      <template #default="{ item }">
        <GwFilterBtnsItem
          :field
          :item="item"
          tstPrefix="filterselection"
        ></GwFilterBtnsItem>
        <div class="icon_btn mdi mdi-close ml-1"></div>
      </template>
      <template #after>
        <LoaderCircles
          v-if="loading"
          class="my-auto mx-1"
          style="--size: 1rem"
        />
      </template>
    </GwBtns>

    <GwBtns
      v-if="field.selection === 'GwBtnsExposed'"
      class="gwbtns_horizontal_grey"
      style="--gwbtns-wrap: wrap"
      :options="[field.isMultiple && 'any', ...field.options].filter(i => i)"
      :values="field.isMultiple ? (!field.values?.length ? ['any'] : field.values) : []"
      :value="field.isSingle ? field.value || field.options[0] : ''"
      :idKey="field.idKey"
      :tstPrefix="`filterselection_${field.field_name}`"
      @click.stop="
        (_e, item) => {
          if (item === 'any') {
            handleUpdate({ field, values: [] })
          } else {
            handleUpdate({ field, item })
          }
        }
      "
    >
      <template #default="{ item }">
        <span v-if="item === 'any'">{{ t('common.any') }}</span>
        <GwFilterBtnsItem
          v-else
          :field
          :item="item"
          tstPrefix="filterselection"
        ></GwFilterBtnsItem>
      </template>
    </GwBtns>

    <!-- BOARD -->
    <div
      v-else-if="field.filterPart && field.selection === 'CardsRowSimple'"
      class="gw_btn text-small weight_500"
      @click="handleUpdate({ field, item: '' })"
    >
      <CardRowSimple
        tstPrefix="filterselection_filterboard"
        :board="field.value || ''"
        style="--crds-card-height: 1.5rem; --crds-card-value-size-increase: 0.9"
      >
      </CardRowSimple>
      <div class="icon_btn mdi mdi-close ml-1"></div>
    </div>

    <!-- HAND -->
    <div
      v-else-if="field.filterPart && field.selection === 'HandsRowMaxed'"
      class="gw_btn text-small weight_500"
      :data-tst="`filterselection_filterhand_${field.values?.join(',')}`"
      @click="handleUpdate({ field, values: [] })"
    >
      {{ field.values?.slice(0, 5).join(', ') }}
      <span
        v-if="field.values && field.values.length > 5"
        class="clr-grey ml-1"
        >+{{ field.values?.length - 5 }}</span
      >
      <div class="icon_btn mdi mdi-close ml-1"></div>
    </div>

    <!-- DATETIME -->
    <div
      v-else-if="field.filterPart && field.selection === 'Datepicker'"
      class="gw_btn text-small weight_500"
      :data-tst="`filterselection_filterdate_${field.rangeValues?.[0]}_${field.rangeValues?.[1]}`"
      @click="handleUpdate({ field, rangeValues: [] })"
    >
      {{ `${formatDate(field.rangeValues?.[0])} - ${formatDate(field.rangeValues?.[1])}` }}
      <div class="icon_btn mdi mdi-close ml-1"></div>
    </div>

    <!-- RANGE_SELECT -->
    <div
      v-else-if="field.filterPart && field.selection === 'GwRangeSelect'"
      class="gw_btn text-small weight_500"
      :data-tst="`filterselection_filterrange_${field.rangeValues?.[0]}_${field.rangeValues?.[1]}`"
      @click="handleUpdate({ field, rangeValues: [] })"
    >
      {{ field.rangeValues?.[0] }}
      {{ field.rangeValues?.[0] && field.rangeValues[1] ? '-' : '' }}
      {{ field.rangeValues?.[1] }}

      <div class="icon_btn mdi mdi-close ml-1"></div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.gw_filter_selectedoptions_content {
  display: flex;
  justify-content: center;
  // align-items: center;
  max-height: 3.125rem;
  overflow: auto;
}
</style>
